import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useQueryParams } from '@shared/hooks/useQueryParams'
import { capitalizeString } from '@shared/utils'

import { ImportProspectsQsFilterKey, ImportProspectsRequestTypes, ImportProspectsStatus } from './types'

interface UsePreselectFiltersProps {
  onSelectEvent: (eventId: string) => void
  onSelectFilters: (filterIds: (number | ImportProspectsRequestTypes | ImportProspectsStatus)[]) => void
  enablePreselect: boolean
}

export const usePreselectFilters = ({ onSelectEvent, onSelectFilters, enablePreselect }: UsePreselectFiltersProps) => {
  const navigate = useNavigate()
  const location = useLocation()

  const { get } = useQueryParams()

  const parseMultipleValue = (queryValue: string | null) => (typeof queryValue === 'string' ? queryValue.split(',') : null)

  const eventIdFilterFromQs = get(ImportProspectsQsFilterKey.event)
  const requestsFiltersFromQs = parseMultipleValue(get(ImportProspectsQsFilterKey.requests)) ?? []
  const statusFiltersFromQs = parseMultipleValue(get(ImportProspectsQsFilterKey.status)) ?? []
  const tagsFiltersFromQs = parseMultipleValue(get(ImportProspectsQsFilterKey.tags)) ?? []

  useEffect(() => {
    if (!enablePreselect || !eventIdFilterFromQs) {
      return
    }

    if (Number.isNaN(Number(eventIdFilterFromQs))) {
      return
    }

    const eventId = eventIdFilterFromQs

    onSelectEvent(eventId)
    let requestsFilters = []
    if (requestsFiltersFromQs?.[0] === 'allRequests') {
      requestsFilters = Object.values(ImportProspectsRequestTypes)
    } else {
      requestsFilters = requestsFiltersFromQs
        .map(capitalizeString)
        .filter(value =>
          Object.values(ImportProspectsRequestTypes).includes(value as ImportProspectsRequestTypes),
        ) as ImportProspectsRequestTypes[]
    }

    let filterStatus = []
    if (statusFiltersFromQs?.[0] === 'allStatuses') {
      filterStatus = Object.values(ImportProspectsStatus)
    } else {
      filterStatus = (statusFiltersFromQs ?? [])
        .map(capitalizeString)
        .filter(value => Object.values(ImportProspectsStatus).includes(value as ImportProspectsStatus)) as ImportProspectsStatus[]
    }

    const tagsFilters = (tagsFiltersFromQs ?? [])
      .filter(value => {
        const tagId = Number(value)
        return !!value && !Number.isNaN(tagId)
      })
      .map(id => (id === '-1' ? 0 : Number(id))) as number[]

    onSelectFilters([...requestsFilters, ...filterStatus, ...tagsFilters])
    navigate(location.pathname, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdFilterFromQs])
}
